import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Country, State } from 'country-state-city';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BillingInformation } from '../../models';
import { useReadOnlyMode } from '../../routes';
import { fromBillingInformation } from '../../store';
import { BillingInformationComponent } from '../../components';

const useStyles = makeStyles(() => ({
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    margin: '26px 0 12px',
  },
  headerRowEdit: {
    margin: '30px 0 3px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  sectionTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 15,
    fontWeight: 700,
    color: '#16141d',
  },
  savedPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12.5,
    fontWeight: 700,
    color: '#0f8461',
    backgroundColor: '#e7f8f1',
    border: '1px solid #b6ebd6',
    padding: '3px 10px',
    borderRadius: 999,
  },
  linkButton: {
    border: 'none',
    background: 'none',
    color: '#0f8461',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 4,
    fontFamily: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#9aa5a0',
      cursor: 'not-allowed',
      textDecoration: 'none',
    },
  },
  cancelLink: {
    color: '#5b6661',
  },
  description: {
    fontSize: 12.8,
    color: '#5b6661',
    marginBottom: 18,
  },
  summaryCard: {
    border: '1px solid #e1e7e4',
    borderRadius: 14,
    backgroundColor: '#fff',
    padding: '4px 18px',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 20,
    padding: '13px 0',
    borderBottom: '1px solid #f3f2f8',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  summaryLabel: {
    fontSize: 13,
    color: '#5b6661',
  },
  summaryValue: {
    fontSize: 13.5,
    fontWeight: 600,
    color: '#16141d',
    textAlign: 'right',
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  saveButton: {
    width: '100%',
    height: 50,
    marginTop: 26,
    border: 'none',
    borderRadius: 12,
    backgroundColor: '#0f8461',
    color: '#fff',
    fontSize: 14.5,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#e7ebe9',
      color: '#9aa5a0',
      cursor: 'not-allowed',
    },
  },
  // Payment & billing redesign form styling, scoped to the embedded billing
  // block — mirrors the checkout redesign so both surfaces stay consistent.
  formScope: {
    '& #billing-information': {
      paddingTop: 0,
    },
    // The legacy form layout pads every row (8px) and the whole block (16px);
    // the design wants description → first field = 18px and last field →
    // save button = 26px, so strip the leading/trailing padding here.
    '& #billing-information-form-layout': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& #billing-information-form-layout > div:first-child': {
      paddingTop: 0,
    },
    '& #billing-information-form-layout > div:last-child': {
      paddingBottom: 0,
    },
    // Column gap: design uses 18px between the two columns, the legacy
    // layout margins add up to 32px — tighten to 9px per side.
    '& #billing-information-form-layout-fullBusinessName, & #billing-information-form-layout-country, & #billing-information-form-layout-city': {
      marginRight: 9,
    },
    '& #billing-information-form-layout-ccEmail, & #billing-information-form-layout-state, & #billing-information-form-layout-postalCode': {
      marginLeft: 9,
    },
    '& #billing-information .MuiTypography-h3, & #billing-information .MuiTypography-h6': {
      fontFamily: "'Inter Tight', sans-serif",
      fontSize: 15,
      fontWeight: 700,
      color: '#16141d',
    },
    '& #billing-information .MuiTypography-subtitle2': {
      fontSize: 12.8,
      color: '#5b6661',
      fontStyle: 'normal',
    },
    '& #billing-information .MuiTypography-body1': {
      fontSize: 12.5,
      fontWeight: 600,
      color: '#3c3a50',
      marginBottom: 6,
    },
    '& #billing-information .MuiOutlinedInput-root': {
      minHeight: 44,
      borderRadius: 8,
      fontSize: 14,
      color: '#16141d',
      backgroundColor: '#fff',
      transition: 'border-color .15s ease, box-shadow .15s ease',
    },
    '& #billing-information .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e1e7e4',
    },
    '& #billing-information .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cdd6d1',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0f8461',
      borderWidth: 1,
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e5484d',
    },
    '& #billing-information .MuiOutlinedInput-root.Mui-error': {
      backgroundColor: '#fff6f6',
    },
    '& #billing-information .MuiOutlinedInput-root:has(input:-webkit-autofill)': {
      backgroundColor: '#e8f0fe',
    },
    '& #billing-information input:-webkit-autofill, & #billing-information input:-webkit-autofill:hover, & #billing-information input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #e8f0fe inset',
      WebkitTextFillColor: '#16141d',
      caretColor: '#16141d',
      borderRadius: 'inherit',
    },
  },
}));

const CheckIcon = () => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const SUMMARY_FIELDS: Array<{ key: keyof BillingInformation; labelId: string }> = [
  { key: 'fullBusinessName', labelId: 'billinginfo.billingcontact.fullBusinessName' },
  { key: 'ccEmail', labelId: 'billinginfo.billingcontact.ccEmail' },
  { key: 'country', labelId: 'billinginfo.billingcontact.country' },
  { key: 'state', labelId: 'billinginfo.billingcontact.state' },
  { key: 'city', labelId: 'billinginfo.billingcontact.city' },
  { key: 'postalCode', labelId: 'billinginfo.billingcontact.postalCode' },
  { key: 'address1', labelId: 'billinginfo.billingcontact.address1' },
  { key: 'address2', labelId: 'billinginfo.billingcontact.address2' },
  { key: 'vatNumber', labelId: 'billinginfo.billingcontact.vatNumber' },
];

export const BillingInfoSection = ({ accountId }: { accountId: number }) => {
  const classes = useStyles();
  const intl = useIntl();
  const isReadOnly = useReadOnlyMode();
  const currentBillingInformation = useSelector(fromBillingInformation.selectOneByAccountId(
    Number(accountId),
  ));
  const isNotSyncedBillingInfo = useSelector(
    fromBillingInformation.isNotSyncedAddressByAccountId(Number(accountId)),
  ) ?? false;
  const billingRef = useRef<any>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // The saved pill is transient feedback — hide it again after a short while.
  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = setTimeout(() => setJustSaved(false), 5000);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const hasSaved = !!currentBillingInformation;
  // Out-of-sync stored addresses must be re-confirmed, so force the form open.
  const isEditMode = editing || !hasSaved || isNotSyncedBillingInfo;

  const handleSave = async () => {
    if (!billingRef.current) return;
    setSaving(true);
    try {
      const saved: boolean = await billingRef.current.validateAndSave();
      if (saved) {
        setEditing(false);
        setJustSaved(true);
      }
    } finally {
      setSaving(false);
    }
  };

  // Empty values render as an empty cell, mirroring the design.
  const displayValue = (key: keyof BillingInformation): string => {
    const raw = (currentBillingInformation?.[key] ?? '').toString();
    if (!raw) return '';
    if (key === 'country') {
      return Country.getCountryByCode(raw)?.name ?? raw;
    }
    if (key === 'state' && currentBillingInformation?.country) {
      const state = State.getStatesOfCountry(currentBillingInformation.country)
        .find(it => it.isoCode === raw || it.name === raw);
      return state?.name ?? raw;
    }
    return raw;
  };

  // Strip the asterisk baked into some translation labels — the summary view
  // shows plain field names.
  const renderLabel = (labelId: string) => intl
    .formatMessage({ id: labelId })
    .replace(/\*\s*$/, '')
    .trim();

  return (
    <Box id="billing-info-section">
      <Box className={`${classes.headerRow} ${isEditMode ? classes.headerRowEdit : ''}`}>
        <Box className={classes.headerLeft}>
          <Typography className={classes.sectionTitle}>
            <FormattedMessage id="billing.information" />
          </Typography>
          {!isEditMode && justSaved && (
            <Box component="span" role="status" className={classes.savedPill}>
              <CheckIcon />
              <FormattedMessage id="page.payment.billing.saved" />
            </Box>
          )}
        </Box>
        {isEditMode
          ? hasSaved && !isNotSyncedBillingInfo && (
            <button
              type="button"
              className={`${classes.linkButton} ${classes.cancelLink}`}
              onClick={() => setEditing(false)}
            >
              <FormattedMessage id="page.payment.method.cancel" />
            </button>
          )
          : (
            <button
              type="button"
              id="billing-info-edit-btn"
              className={classes.linkButton}
              onClick={() => setEditing(true)}
              disabled={isReadOnly}
            >
              <FormattedMessage id="page.payment.method.edit" />
            </button>
          )}
      </Box>
      {isEditMode && (
        <Typography className={classes.description}>
          <FormattedMessage id="billinginfo.billingcontact.description" />
        </Typography>
      )}
      {isEditMode
        ? (
          <Box className={classes.formScope}>
            <BillingInformationComponent
              ref={billingRef}
              accountId={accountId}
              enableSave
              currentBillingInformation={currentBillingInformation}
              redesign
              hideHeader
            />
            <button
              type="button"
              id="billing-info-save-btn"
              className={classes.saveButton}
              onClick={() => {
                handleSave().catch(() => {
                });
              }}
              disabled={saving || isReadOnly}
            >
              <FormattedMessage
                id={hasSaved ? 'page.payment.billing.update' : 'page.payment.billing.save'}
              />
            </button>
          </Box>
        )
        : (
          <Box className={classes.summaryCard} id="billing-info-summary">
            {SUMMARY_FIELDS.map(({ key, labelId }) => (
              <Box key={key} className={classes.summaryRow}>
                <Box component="span" className={classes.summaryLabel}>
                  {renderLabel(labelId)}
                </Box>
                <Box component="span" className={classes.summaryValue}>
                  {displayValue(key)}
                </Box>
              </Box>
            ))}
          </Box>
        )}
    </Box>
  );
};
